if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        if (!this.form) return;

        const variantInput = this.form.querySelector('[name=id]');
        if (variantInput) variantInput.disabled = false;

        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        // Prefer drawer for live ATC updates (LoveLab uses cart drawer)
        this.cart =
          document.querySelector('cart-drawer') || document.querySelector('cart-notification');
        this.submitButton = this.querySelector('[type="submit"]');
        if (this.submitButton && document.querySelector('cart-drawer')) {
          this.submitButton.setAttribute('aria-haspopup', 'dialog');
        }

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      imageBasename(url) {
        try {
          const path = new URL(url, window.location.origin).pathname;
          return decodeURIComponent(path.split('/').pop() || '').toLowerCase();
        } catch (e) {
          const raw = String(url || '').split('?')[0];
          return decodeURIComponent(raw.split('/').pop() || '').toLowerCase();
        }
      }

      isLifestyleImageUrl(url) {
        const file = this.imageBasename(url);
        if (!file) return false;
        if (/lifestyle|banner/.test(file)) return true;
        return /^(sparkle_round|shapy_sparkle_round_2|round_sparkle|pear_sparkle|oval_sparkle|sparkle_heart|sparkle_oval|sparkle_emerald|sparkle_marq|princesse_sparkle|longcushion_sparkle|shine_heart|shine_pear|shine_bezel_heart|shine_oval|shapyshine|shapy_pear_marq|emerald_rotate|marq_shine|oval_shine|longcushion_shine|matchy_heart\.png|matchy_pear\.png|matchy_emerald\.jpg|matchy_pear_bezel|necklace_banner|three_white|three_pink|three_yellow|three_mix|triplymix|holy_lifestyle|cubix\.jpeg|cubix_2)/.test(
          file
        );
      }

      getSelectedGalleryImageUrl() {
        // Only the jewelry packshot slot — never 2nd/3rd gallery model photos.
        const img =
          document.getElementById('ll-cuty-main-image') ||
          document.getElementById('ll-cubix-main-image') ||
          document.querySelector('[data-ll-cart-photo]');
        if (!img) return '';
        const url = (img.currentSrc || img.src || '').trim();
        if (!url || url.startsWith('data:')) return '';
        if (this.isLifestyleImageUrl(url)) return '';
        try {
          return this.toPublicImageUrl(new URL(url, window.location.origin).href);
        } catch (e) {
          return this.toPublicImageUrl(url);
        }
      }

      /**
       * Prefer a publicly reachable URL (CDN), not theme:dev localhost.
       */
      toPublicImageUrl(url) {
        if (!url) return '';
        try {
          const parsed = new URL(url, window.location.origin);
          const isLocal =
            parsed.hostname === '127.0.0.1' ||
            parsed.hostname === 'localhost' ||
            parsed.port === '9292';

          if (!isLocal) return parsed.href;

          // theme:dev often proxies CDN as /cdn/shop/...
          if (parsed.pathname.startsWith('/cdn/')) {
            return `https://cdn.shopify.com${parsed.pathname.replace(/^\/cdn/, '')}${parsed.search}`;
          }

          if (window.Shopify && Shopify.shop) {
            return `https://${Shopify.shop}${parsed.pathname}${parsed.search}`;
          }

          return parsed.href;
        } catch (e) {
          return url;
        }
      }

      syncCartImageProperty(formData) {
        const liveUrl = this.getSelectedGalleryImageUrl();
        const hidden = this.form.querySelector('[data-ll-cart-image], input[name="properties[_ll_image]"]');
        const fallback = hidden && hidden.value && !this.isLifestyleImageUrl(hidden.value) ? hidden.value : '';
        const imageUrl = liveUrl || fallback;
        if (hidden) hidden.value = imageUrl || '';
        if (imageUrl) {
          formData.set('properties[_ll_image]', imageUrl);
        } else {
          formData.delete('properties[_ll_image]');
        }
      }

      getSectionsPayload() {
        if (!this.cart || typeof this.cart.getSectionsToRender !== 'function') {
          return { sectionIds: [], sectionsUrl: '/' };
        }
        const sectionIds = this.cart.getSectionsToRender().map((section) => section.id);
        const sectionsUrl =
          (window.Shopify && Shopify.routes && Shopify.routes.root) || window.location.pathname || '/';
        if (typeof this.cart.setActiveElement === 'function') {
          this.cart.setActiveElement(document.activeElement);
        }
        return { sectionIds, sectionsUrl };
      }

      async parseCartResponse(response) {
        const text = await response.text();
        let data = null;
        try {
          data = text ? JSON.parse(text) : null;
        } catch (e) {
          const msg =
            response.status === 429
              ? (window.llI18n && window.llI18n.cartTooManyRequests) || 'Too many requests. Please wait 1–2 minutes and try again.'
              : response.status === 401
                ? (window.llI18n && window.llI18n.cartSessionExpired) || 'Session expired. Refresh the page and try again.'
                : ((window.llI18n && window.llI18n.cartAddFailed) || 'Could not add to cart ({{ status }}). Please refresh and try again.').replace('{{ status }}', response.status);
          this.error = true;
          this.handleErrorMessage(msg);
          throw new Error(msg);
        }

        if (!response.ok && !data?.status) {
          this.error = true;
          this.handleErrorMessage(data?.description || data?.message || ((window.llI18n && window.llI18n.cartError) || 'Cart error ({{ status }})').replace('{{ status }}', response.status));
          return null;
        }

        return data;
      }

      /**
       * After the main product is added, add the Engraving fee line (non-blocking for drawer).
       * Returns merged sections from the fee add when available.
       */
      async addEngravingFeeAfterParent(formData, sectionIds, sectionsUrl) {
        if (!window.llEngravingCart || typeof window.llEngravingCart.buildFeeItem !== 'function') {
          return null;
        }
        const feeItem = window.llEngravingCart.buildFeeItem(formData);
        if (!feeItem) return null;

        try {
          const payload = { items: [feeItem] };
          if (sectionIds && sectionIds.length) {
            payload.sections = sectionIds.join(',');
            payload.sections_url = sectionsUrl || '/';
          }
          const response = await fetch(`${routes.cart_add_url}`, {
            ...fetchConfig('json'),
            body: JSON.stringify(payload),
          });
          const text = await response.text();
          let data = null;
          try {
            data = text ? JSON.parse(text) : null;
          } catch (e) {
            console.warn('[ll-engraving] fee line add returned non-JSON', response.status);
            return null;
          }
          if (!response.ok || data?.status) {
            console.warn('[ll-engraving] fee line add failed', data?.description || data || response.status);
            return null;
          }
          return data;
        } catch (e) {
          console.warn('[ll-engraving] fee line add error', e);
          return null;
        }
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (!this.submitButton || this.submitButton.getAttribute('aria-disabled') === 'true') return;

        const engravingInput = this.form.querySelector('[name="properties[Engraving]"]');
        const engravingValue = engravingInput ? engravingInput.value.trim() : '';
        if (engravingValue && !this._engravingConfirmed) {
          this.showEngravingConfirmation(engravingValue);
          return;
        }
        this._engravingConfirmed = false;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        const spinner = this.querySelector('.loading-overlay__spinner');
        if (spinner) spinner.classList.remove('hidden');

        const formData = new FormData(this.form);
        this.syncCartImageProperty(formData);

        // Link parent + fee lines when engraving is present
        let engravingLinkId = null;
        if (
          engravingValue &&
          window.llEngravingCart &&
          typeof window.llEngravingCart.getVariantId === 'function' &&
          window.llEngravingCart.getVariantId()
        ) {
          engravingLinkId = window.llEngravingCart.createLinkId();
          formData.set('properties[_engraving_id]', engravingLinkId);
        }

        const sellingPlanId =
          typeof window.getCurrentSellingPlanId === 'function' ? window.getCurrentSellingPlanId() : '';
        if (sellingPlanId) formData.append('selling_plan', sellingPlanId);

        const { sectionIds, sectionsUrl } = this.getSectionsPayload();
        if (sectionIds.length) {
          formData.append('sections', sectionIds.join(','));
          formData.append('sections_url', sectionsUrl);
        }

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => this.parseCartResponse(response))
          .then(async (response) => {
            if (!response) return;

            if (response.status) {
              try {
                publish(PUB_SUB_EVENTS.cartError, {
                  source: 'product-form',
                  productVariantId: formData.get('id'),
                  errors: response.description,
                  message: response.message,
                });
              } catch (e) {
                console.error(e);
              }
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              const label = this.submitButton.querySelector('span');
              if (label) label.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            }

            // Parent added — try fee line next (drawer still opens even if fee fails)
            if (engravingLinkId) {
              formData.set('properties[_engraving_id]', engravingLinkId);
              const feeResponse = await this.addEngravingFeeAfterParent(formData, sectionIds, sectionsUrl);
              if (feeResponse && feeResponse.sections) {
                response.sections = Object.assign({}, response.sections || {}, feeResponse.sections);
              }
            }

            if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            this.error = false;
            if (window.llAddedToBagToast && typeof window.llAddedToBagToast.showFromCartLine === 'function') {
              window.llAddedToBagToast.showFromCartLine(response);
            }
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }

            try {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
              });
            } catch (e) {
              console.error(e);
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) {
              this.cart.classList.remove('is-empty');
            }
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            if (spinner) spinner.classList.add('hidden');
          });
      }

      showEngravingConfirmation(letters) {
        const existing = document.getElementById('ll-engraving-confirm-overlay');
        if (existing) existing.remove();

        const overlay = document.createElement('div');
        overlay.id = 'll-engraving-confirm-overlay';
        overlay.style.cssText = 'position:fixed;inset:0;z-index:9999;display:flex;align-items:center;justify-content:center;background:rgba(0,0,0,0.45);';

        overlay.innerHTML = `
          <div style="background:#fff;border-radius:16px;padding:32px 28px 24px;max-width:380px;width:90%;box-shadow:0 20px 60px rgba(0,0,0,0.15);text-align:center;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;">
            <div style="width:48px;height:48px;border-radius:50%;background:#fffbeb;display:flex;align-items:center;justify-content:center;margin:0 auto 16px;">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#d97706" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
            </div>
            <h3 style="margin:0 0 8px;font-size:18px;font-weight:600;color:#1e1e1e;">${(window.llI18n && window.llI18n.confirmEngraving) || 'Confirm Engraving'}</h3>
            <p style="margin:0 0 6px;font-size:14px;color:#555;line-height:1.5;">${(window.llI18n && window.llI18n.yourEngraving) || 'Your engraving:'} <strong style="letter-spacing:0.15em;font-size:16px;">${letters}</strong> <span style="color:#92400e;font-size:13px;">(+${(function(){var cents=(window.theme&&typeof theme.getEngravingCents==='function')?theme.getEngravingCents():750;if(window.theme&&typeof theme.formatMoneyExact==='function')return theme.formatMoneyExact(cents);if(typeof Shopify!=='undefined'&&Shopify.formatMoney&&window.theme&&theme.moneyFormat)return Shopify.formatMoney(cents,theme.moneyFormat);return ((window.theme&&theme.currencySymbol)||'€')+(cents%100?(cents/100).toFixed(2).replace('.',','):String(cents/100));})()})</span></p>
            <p style="margin:0 0 20px;font-size:13px;color:#92400e;line-height:1.5;">${(window.llI18n && window.llI18n.engravedReturns) || 'Engraved items cannot be returned or exchanged.'}</p>
            <div style="display:flex;gap:10px;">
              <button type="button" id="ll-eng-cancel" style="flex:1;height:44px;border-radius:10px;border:1px solid #e5e5e5;background:#fff;font-size:14px;font-weight:500;cursor:pointer;color:#555;">${(window.llI18n && window.llI18n.cancel) || 'Cancel'}</button>
              <button type="button" id="ll-eng-confirm" style="flex:1;height:44px;border-radius:10px;border:none;background:hsl(299,20%,31%);color:#fff;font-size:14px;font-weight:600;cursor:pointer;">${(window.llI18n && window.llI18n.confirmAdd) || 'Confirm & Add'}</button>
            </div>
          </div>`;

        document.body.appendChild(overlay);

        overlay.querySelector('#ll-eng-cancel').addEventListener('click', () => overlay.remove());
        overlay.addEventListener('click', (e) => { if (e.target === overlay) overlay.remove(); });

        overlay.querySelector('#ll-eng-confirm').addEventListener('click', () => {
          overlay.remove();
          this._engravingConfirmed = true;
          this.form.requestSubmit ? this.form.requestSubmit() : this.form.dispatchEvent(new Event('submit', { bubbles: true }));
        });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage =
          this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }
    }
  );
}
